/**
 * Выбор текста отказа для пользователя.
 *
 * Норма: spec/invariants/api-errors.md
 *  - api-errors-R04 — пользователь видит отказ на языке интерфейса; текст собирает
 *    клиент по машиночитаемому признаку причины и параметрам подстановки.
 *    Серверное сообщение — ЗАПАСНОЙ текст на случай, когда по признаку причины
 *    показать нечего.
 *  - api-errors-R08 — подмена причины обобщённым текстом недопустима.
 *
 * Функция чистая: перевод передаётся снаружи, чтобы её можно было проверить
 * без vue-i18n и чтобы слой отображения не тянулся в модель.
 */

import type { IApiError } from "@/shared/types";

/**
 * Ищет перевод по ключу. Возвращает `undefined`, если ключа нет —
 * тогда выбирается следующий кандидат.
 */
export type TApiErrorTranslate = (
  key: string,
  params: Record<string, unknown>
) => string | undefined;

/** Ключ обобщённого текста — применяется, только когда показать больше нечего. */
export const UNKNOWN_ERROR_TEXT_KEY = "errors.UNKNOWN_ERROR";

/**
 * Суффикс варианта текста для случая, когда параметров подстановки нет.
 *
 * Одна и та же причина (api-errors-R07: одна причина — один признак) приходит
 * то с названным объектом, то без него: продукт не всегда может назвать поле
 * или параметр. Текст с подстановкой в этом случае показал бы пустое место,
 * поэтому у такой причины есть второй, безымянный вариант текста —
 * `errors.<признак>_UNNAMED`. Признак причины при этом остаётся один.
 */
export const UNNAMED_TEXT_SUFFIX = "_UNNAMED";

/**
 * Собирает ключи-кандидаты для признака причины: сначала зональные
 * (`mapping.errors.NAME_TAKEN`), затем общий (`errors.NAME_TAKEN`).
 */
export function apiErrorTextKeys(
  code: string,
  namespaces: readonly string[]
): readonly string[] {
  const keys = namespaces.map((ns) => `${ns}.errors.${code}`);
  keys.push(`errors.${code}`);
  return keys;
}

/**
 * Возвращает текст отказа на языке интерфейса.
 *
 * Порядок: перевод по признаку причины (с параметрами) → запасное сообщение
 * продукта → обобщённый текст. Признак причины в текст не подставляется:
 * он не является сообщением (api-errors-R07).
 *
 * Отказ без параметров подстановки сначала ищет безымянный вариант текста
 * (`<признак>_UNNAMED`), а если его нет — обычный.
 */
export function resolveApiErrorText(
  error: IApiError | null | undefined,
  translate: TApiErrorTranslate,
  namespaces: readonly string[] = []
): string {
  const fallback = translate(UNKNOWN_ERROR_TEXT_KEY, {}) ?? UNKNOWN_ERROR_TEXT_KEY;

  if (error === null || error === undefined) {
    return fallback;
  }

  const params = error.params ?? {};

  if (error.code.length > 0) {
    const codes =
      Object.keys(params).length > 0
        ? [error.code]
        : [`${error.code}${UNNAMED_TEXT_SUFFIX}`, error.code];

    for (const code of codes) {
      for (const key of apiErrorTextKeys(code, namespaces)) {
        const text = translate(key, params);
        if (text !== undefined && text.length > 0) {
          return text;
        }
      }
    }
  }

  if (error.message.length > 0) {
    return error.message;
  }

  return fallback;
}
